/**
 * Offerteaanvraag (RFQ) naar leveranciers - aanhef en mailopbouw.
 *
 * Een aanvraag kan in een keer naar meerdere leveranciers. Elke leverancier
 * krijgt een eigen mail met een eigen aanhef en alleen zijn eigen adres in
 * "Aan": niemand ziet wie er nog meer benaderd is. Deze module is puur (geen
 * DB, geen I/O), zodat de API en de voorbeeldweergave in de backoffice exact
 * dezelfde tekst opbouwen.
 */

import { ymdNaarNl } from './besteladvies'

export interface RfqAanhefContact {
  firstName?: string | null
  lastName?: string | null
}

/** Voornaam die alleen uit initialen bestaat ("T.", "J.P.") is geen voornaam. */
function isInitialen(naam: string): boolean {
  return /^[A-Za-z]$/.test(naam) || /^([A-Za-z]\.\s*)+$/.test(naam)
}

/**
 * Aanhef voor een leverancier-mail (zonder komma).
 * - contact met voornaam -> "Beste Tom"
 * - alleen initialen/achternaam -> "Geachte heer/mevrouw Schoonenberg"
 * - geen contact -> "Geachte heer/mevrouw"
 */
export function rfqAanhef(contact: RfqAanhefContact | null | undefined): string {
  const voornaam = (contact?.firstName ?? '').trim()
  const achternaam = (contact?.lastName ?? '').trim()
  if (voornaam && !isInitialen(voornaam)) return `Beste ${voornaam}`
  if (achternaam) return `Geachte heer/mevrouw ${achternaam}`
  return 'Geachte heer/mevrouw'
}

export interface RfqMailRegel {
  omschrijving: string
  aantal: number
  eenheid?: string | null
  artikelnummer?: string | null
  merk?: string | null
  specificatie?: string | null
  /** Bestandsnamen van de foto's van deze regel zoals ze als bijlage meegaan. */
  fotoNamen?: string[]
}

export interface RfqMailInput {
  rfqNummer: string
  aanhef: string
  regels: RfqMailRegel[]
  gewensteLevertijd?: string | null
  afleveradres?: string | null
  /** Deadline als jjjj-mm-dd. */
  deadlineYmd?: string | null
  /** Vaste toelichting op de aanvraag (opmerking). */
  opmerking?: string | null
  /** Losse toevoeging bij het versturen. */
  extraBericht?: string | null
}

function escapeHtml(s: string | null | undefined): string {
  return (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Meerregelige tekst veilig als HTML met regeleinden. */
function tekstNaarHtml(s: string): string {
  return escapeHtml(s).replace(/\r?\n/g, '<br/>')
}

/** Bestandsnaam van een foto-bijlage: "regel-2-foto-1.jpg". */
export function rfqFotoBijlageNaam(regelNr: number, fotoNr: number, mimeType: string): string {
  const ext = mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg'
  return `regel-${regelNr}-foto-${fotoNr}.${ext}`
}

export function rfqMailOnderwerp(rfqNummer: string): string {
  return `Offerteaanvraag ${rfqNummer} - STREEP`
}

/**
 * Bouwt de HTML-body van de offerteaanvraag voor een leverancier. Bevat bewust
 * geen namen van andere leveranciers, geen interne prijzen en geen huidige
 * leverancier van een bestaand artikel.
 */
export function bouwRfqMailHtml(input: RfqMailInput): string {
  const rijen = input.regels
    .map((r, i) => {
      const details: string[] = []
      if (r.merk) details.push(`Merk/type: ${escapeHtml(r.merk)}`)
      if (r.artikelnummer) details.push(`Artikelnummer: ${escapeHtml(r.artikelnummer)}`)
      if (r.specificatie) details.push(tekstNaarHtml(r.specificatie))
      if (r.fotoNamen && r.fotoNamen.length > 0) {
        details.push(`Foto in bijlage: ${r.fotoNamen.map(escapeHtml).join(', ')}`)
      }
      const aantal = `${r.aantal}${r.eenheid ? ` ${escapeHtml(r.eenheid)}` : ''}`
      return (
        '<tr>' +
        `<td style="padding:6px 8px;border-bottom:1px solid #CED0C8;vertical-align:top">${i + 1}</td>` +
        `<td style="padding:6px 8px;border-bottom:1px solid #CED0C8;vertical-align:top;white-space:nowrap">${aantal}</td>` +
        `<td style="padding:6px 8px;border-bottom:1px solid #CED0C8;vertical-align:top"><strong>${escapeHtml(r.omschrijving)}</strong>` +
        (details.length ? `<br/><span style="color:#5C5C58">${details.join('<br/>')}</span>` : '') +
        '</td></tr>'
      )
    })
    .join('')

  const tabel =
    '<table style="border-collapse:collapse;width:100%;max-width:640px;font-size:14px">' +
    '<thead><tr style="background:#F0F1EC;text-align:left">' +
    '<th style="padding:6px 8px">#</th><th style="padding:6px 8px">Aantal</th><th style="padding:6px 8px">Artikel</th>' +
    `</tr></thead><tbody>${rijen}</tbody></table>`

  const voorwaarden: string[] = []
  if (input.gewensteLevertijd) voorwaarden.push(`Gewenste levertijd: ${escapeHtml(input.gewensteLevertijd)}`)
  if (input.afleveradres) voorwaarden.push(`Afleveradres: ${escapeHtml(input.afleveradres)}`)
  if (input.deadlineYmd) voorwaarden.push(`Graag uw offerte uiterlijk ${escapeHtml(ymdNaarNl(input.deadlineYmd))}.`)

  return (
    `<p>${escapeHtml(input.aanhef)},</p>` +
    '<p>Graag ontvangen wij van u een offerte voor onderstaande artikelen. ' +
    `Wilt u in uw reactie het referentienummer <strong>${escapeHtml(input.rfqNummer)}</strong> vermelden?</p>` +
    tabel +
    (voorwaarden.length ? `<p>${voorwaarden.join('<br/>')}</p>` : '') +
    (input.opmerking ? `<p>${tekstNaarHtml(input.opmerking)}</p>` : '') +
    (input.extraBericht ? `<p>${tekstNaarHtml(input.extraBericht)}</p>` : '') +
    '<p>Vermeld bij voorkeur per regel de prijs per stuk, de levertijd en de geldigheid van de offerte. ' +
    'U kunt eenvoudig op deze mail antwoorden.</p>' +
    '<p>Met vriendelijke groet,<br/>STREEP Inkoop</p>'
  )
}
